import React, { Fragment, ReactNode } from "react"
import {
  Menu as ChakraMenu,
  MenuButton,
  MenuItem as ChakraMenuItem,
  MenuList,
  MenuDivider as ChakraMenuDivider,
  Heading,
  MenuGroup as ChakraMenuGroup,
  As,
  StyleProps,
  Portal,
  LayoutProps,
  TypographyProps,
  Flex,
  Box,
  Button as ChakraButton,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import menuViewportMaxHeightModifier from "./menuViewportMaxHeight"

// Mirrors SimpleBar's default visible-state thumb (used by Combobox /
// BadgeBuilder / DataTable etc.) so MenuList feels like the same scroll
// family even though it stays native. Geometry: 11px track, 7px effective
// thumb (via a 2px transparent inset border + padding-box clip), rounded
// ends. Color: rgba(0,0,0,0.25) — SimpleBar's 0.5-alpha background under
// its opacity: 0.5 visible state collapses to the same effective alpha.
const SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "rgb(127,127,127) transparent",
  "&::-webkit-scrollbar": {
    width: "11px",
    height: "11px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgb(127,127,127)",
    backgroundClip: "padding-box",
    border: "2px solid transparent",
    borderRadius: "7px",
  },
} as const

type MenuProps = {
  button?: ReactNode
  buttonText?: string
  buttonVariant?: "solid" | "outline" | "ghost"
  buttonWidth?: "full"
  buttonColor?: string
  buttonIcon?: string
  buttonFontWeight?: TypographyProps["fontWeight"]
  menuItems: ReactNode[]
  title?: string;
  gutter?: number
  placement?: "top" | "right" | "bottom" | "left" | "auto" | "auto-start" | "auto-end" | "top-start" | "top-end" | "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" | "left-end"
  noPadding?: boolean
  disabled?: boolean
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  isLazy?: boolean
  autoSelect?: boolean
  maxH?: LayoutProps["maxH"]
  menuListWidth?: LayoutProps["w"]
  closeOnSelect?: boolean
  /** When false, render menu list in place instead of portal (e.g. iframe/preview) */
  usePortal?: boolean
  /** When false, prevent menu from flipping placement (e.g. always open downward) */
  flip?: boolean
}

const Menu = ({
  button,
  buttonText,
  buttonVariant = "outline",
  buttonWidth,
  buttonColor,
  buttonIcon,
  buttonFontWeight,
  menuItems,
  title,
  gutter = 8,
  placement = "bottom-end",
  noPadding = false,
  disabled = false,
  isOpen,
  onOpen,
  onClose,
  isLazy,
  autoSelect = true,
  maxH,
  menuListWidth,
  closeOnSelect = true,
  usePortal = true,
  flip = true,
}: MenuProps) => {
  const popperBoundary = typeof document !== "undefined" ? document.documentElement : "clippingParents"
  const useViewportMaxHeightModifier = maxH === undefined
  const menuListMaxH = maxH
  const menuListModifiers = useViewportMaxHeightModifier
    ? [menuViewportMaxHeightModifier]
    : undefined

  const renderButton = () => ((buttonText || buttonIcon) ? (
    <ChakraButton
      pointerEvents="auto"
      variant={buttonVariant}
      size="sm"
      onClick={onOpen}
      colorScheme={buttonColor ? undefined : "button.primary"}
      color={buttonColor}
      fontWeight={buttonFontWeight}
      bgColor={buttonVariant === "outline" ? "white" : ""}
      w={buttonWidth === "full" ? "100%" : undefined}
    >
      <Flex w="100%" justify="space-between" align="center" gap={2}>
        <Flex align="center" gap={2}>
          {buttonIcon && <Icon icon={buttonIcon} size={4} color="currentColor" />}
          {buttonText && <Box lineHeight="short">{buttonText}</Box>}
        </Flex>
        <Icon icon="down-arrow" size={3} color="currentColor" />
      </Flex>
    </ChakraButton>
  ) : <>{button}</>)

  if (disabled) {
    return renderButton()
  }

  return (
    <ChakraMenu
      gutter={gutter}
      placement={placement}
      flip={flip}
      strategy="fixed"
      boundary={popperBoundary}
      modifiers={menuListModifiers}
      isLazy={isLazy}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      autoSelect={autoSelect}
      closeOnSelect={closeOnSelect}
    >
      <MenuButton w={buttonWidth} maxW="100%" title={title} aria-label={title} type="button" pointerEvents="auto">
        {renderButton()}
      </MenuButton>
      {usePortal ? (
        <Portal>
          <MenuList
            p={noPadding ? 0 : undefined}
            maxH={menuListMaxH}
            minH={0}
            w={menuListWidth}
            maxW="calc(100vw - 1rem)"
            overflowX="hidden"
            overflowY="auto"
            sx={SCROLLBAR_SX}
          >
            {menuItems.map((item, index) => <Fragment key={index}>{item}</Fragment>)}
          </MenuList>
        </Portal>
      ) : (
        <MenuList
          p={noPadding ? 0 : undefined}
          maxH={menuListMaxH}
          minH={0}
          w={menuListWidth}
          maxW="calc(100vw - 1rem)"
          overflowX="hidden"
          overflowY="auto"
          sx={SCROLLBAR_SX}
        >
          {menuItems.map((item, index) => <Fragment key={index}>{item}</Fragment>)}
        </MenuList>
      )}
    </ChakraMenu>
  )
}

type MenuHeaderProps = {
  children: ReactNode;
  noPadding?: boolean;
}

export const MenuHeader = ({ children, noPadding }: MenuHeaderProps) => (
  <Heading textAlign="center" fontSize="md" bg="gray.100" px={0} py={noPadding ? 0 : 3}>{children}</Heading>
)

type MenuGroupProps = {
  title: string;
  children: ReactNode;
  first?: boolean;
}

export const MenuGroup = ({ title, children, first }: MenuGroupProps) => (
  <ChakraMenuGroup
    title={title}
    mx={0}
    p={3}
    borderTopWidth={1}
    borderBottomWidth={1}
    textAlign="center"
    fontSize="md"
    bg="gray.100"
    {...first ? { mt: "-1px" } : {}}
  >
    {children}
  </ChakraMenuGroup>
)

type MenuItemProps = {
  children: ReactNode
  color?: string;
  icon?: string;
  selected?: boolean;
  isDisabled?: boolean;
  isFocusable?: boolean;
  isCentered?: boolean;
  onClick?: () => void;
  to?: string | { pathname: string; state?: unknown };
  iconSize?: number;
  as?: As;
  closeOnSelect?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  sx?: Record<string, unknown>;
} & StyleProps

export const MenuItem = ({
  icon, children, isCentered, selected, iconSize, to, ...rest
}: MenuItemProps) => (
  <ChakraMenuItem
    icon={icon ? <Icon icon={icon} size={iconSize} /> : undefined}
    fontSize="md"
    justifyContent={isCentered ? "center" : undefined}
    px={8}
    py={3}
    to={to}
    {...rest}
  >
    {selected !== undefined && (
      <Icon
        icon="checkmark"
        color="gray.600"
        ml={-4}
        mr={2}
        visibility={selected ? "visible" : "hidden"}
      />
    )}
    {children}
  </ChakraMenuItem>
)

export const MenuDivider = ({ ...props }) => (<ChakraMenuDivider {...props} />)

export default Menu
